import type Echo from '@ably/laravel-echo';
import { type Exchange, subscriptionExchange } from '@urql/core';
import { inject } from 'vue';
import { providerKeys } from '@/composables/useProviders';
import { runInBackground } from '@/helpers/backgroundPromise';

// Lighthouse subscriptions work in two steps:
//   1. An HTTP POST registers the subscription and returns a private Ably channel name.
//   2. The client joins that channel via Laravel Echo and listens for pushed events.
// subscriptionExchange handles the urql integration; we only need to implement forwardSubscription.
export const lighthouseSubscriptionExchange = (url: string, getHeaders: () => Record<string, string>): Exchange =>
    subscriptionExchange({
        forwardSubscription(request) {
            // inject() works here because urql calls forwardSubscription synchronously
            // during useSubscription(), which runs inside a component's <script setup>.
            // Vue's active component instance is still set at this point.
            const echo = inject(providerKeys.echo);
            return {
                subscribe(sink): { unsubscribe(): void; } {
                    // Holds the Echo channel so we can clean it up on unsubscribe.
                    let echoChannel: ReturnType<Echo['private']> | null = null;
                    let channelName: string | null = null;
                    let disposed = false;
                    const controller = new AbortController();

                    // Step 1: POST the subscription query to Lighthouse. It registers the subscription
                    // server-side and returns the Ably channel name in response.extensions.
                    fetch(url, {
                        method: 'POST',
                        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ query: request.query, variables: request.variables }),
                        signal: controller.signal,
                    })
                        .then(r => r.json())
                        .then((response: { extensions?: { lighthouse_subscriptions?: { channel?: string } } }) => {
                            if (disposed) {
                                return;
                            }

                            channelName = response.extensions?.lighthouse_subscriptions?.channel ?? null;
                            if (channelName == null || channelName === '') {
                                sink.error(new Error('Lighthouse did not return a subscription channel'));
                                return;
                            }

                            if (!echo) {
                                sink.error(new Error('Echo not initialized'));
                                return;
                            }

                            // Step 2: Join the private Ably channel and forward each pushed event to urql.
                            // Lighthouse prefixes channel names with "private-"; echo.private() adds it again, so strip it first.
                            echoChannel = echo.private(channelName.replace(/^private-/, ''));
                            echoChannel.listen('.lighthouse-subscription', (data: { data?: object; result?: object }) => {
                                // Normalise to a GraphQL ExecutionResult — Lighthouse may send either shape.
                                sink.next(data.data ?? { data: data.result });
                            });
                        })
                        .catch((err: unknown) => {
                            if (disposed && err instanceof DOMException && err.name === 'AbortError') {
                                return;
                            }
                            sink.error(err instanceof Error ? err : new Error(String(err)));
                        });

                    // Returned immediately so urql can unsubscribe before the fetch resolves.
                    return {
                        unsubscribe(): void {
                            disposed = true;
                            controller.abort();
                            echoChannel?.stopListening('.lighthouse-subscription');
                            if (channelName != null && channelName !== '' && echo != null) {
                                // echo.leave() expects the user-facing name (without the "private-" prefix);
                                // it handles the Ably colon-format conversion internally.
                                echo.leave(channelName.replace(/^private-/, ''));

                                // Explicitly delete the subscription server-side so Lighthouse
                                // removes it from Valkey immediately rather than waiting for the TTL.
                                // Fire-and-forget: if this fails the TTL is the fallback.
                                runInBackground(fetch(`${url}/subscriptions`, {
                                    method: 'DELETE',
                                    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ channel_name: channelName }),
                                }));
                            }
                        },
                    };
                },
            };
        },
    });
